"use client";

import { cn } from "@/lib/utils";
import Link from "next/link";
import { LottiePlayer } from "./lottie/LottiePlayer";

/**
 * SATU implementasi empty state untuk seluruh app.
 *
 * Sebelumnya ada dua komponen dengan nama export sama tapi props tak kompatibel:
 * `ui/EmptyState` (punya `icon` + aksi href/onClick, 9 importer) dan
 * `ui/lottie/EmptyState` (punya animasi Lottie, aksi onClick saja, 26 importer).
 * Varian lottie TIDAK punya prop ikon sama sekali — itulah sebabnya 24 dari 31
 * empty state di admin tampil polos tanpa ikon dan tanpa CTA.
 *
 * Versi gabungan menerima `icon` ATAU `animationSrc`, dan aksi berupa `href`
 * maupun `onClick`. `ui/lottie/EmptyState` kini hanya me-re-export komponen ini,
 * jadi seluruh call site lama tetap jalan tanpa perubahan.
 */
type EmptyStateAction =
  | { label: string; href: string; onClick?: never }
  | { label: string; onClick: () => void; href?: never };

interface EmptyStateProps {
  icon?: React.ReactNode;
  /** Path .lottie/.json di /public/lottie/ atau URL. Diabaikan bila `icon` diisi. */
  animationSrc?: string;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const LOTTIE_SIZES = {
  sm: "w-20 h-20",
  md: "w-28 h-28",
  lg: "w-36 h-36",
} as const;

const ACTION_CLASS =
  "mt-4 inline-flex items-center min-h-11 px-4 bg-primary text-[var(--brand-on-primary,#fff)] text-sm font-semibold rounded-lg hover:bg-primary-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] focus-visible:ring-offset-2";

export function EmptyState({
  icon,
  animationSrc,
  title,
  description,
  action,
  className,
  size = "md",
}: EmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center py-16 px-4 text-center", className)}>
      {icon ? (
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-warm-cream text-[rgba(12,12,10,0.35)]">
          {icon}
        </div>
      ) : animationSrc ? (
        <div className={cn(LOTTIE_SIZES[size], "mb-4")}>
          <LottiePlayer src={animationSrc} loop autoplay />
        </div>
      ) : null}
      <h2 className="mb-1 text-base font-semibold text-warm-black">{title}</h2>
      {description && (
        <p className="max-w-xs text-sm text-[rgba(12,12,10,0.55)]">{description}</p>
      )}
      {action &&
        (action.href ? (
          <Link href={action.href} className={ACTION_CLASS}>
            {action.label}
          </Link>
        ) : (
          <button type="button" onClick={action.onClick} className={ACTION_CLASS}>
            {action.label}
          </button>
        ))}
    </div>
  );
}
